/* =========================================================================
   LSC - Progress Photos (formerly the My Progress tab on the account page).

   Talks to the Cloudflare Worker through the Shopify app proxy. Shopify signs
   every request to that path and adds the shopper's customer id, so this file
   never sends, and never needs to know, who the customer is. Trying to pass
   an id from here would be both pointless and a hole.

   LSC - 23 September 2026, crop editor and photo dates:
   - Every new photo opens a crop editor before it uploads. The crop is a
     fixed 3:4 portrait frame with a centred face outline drawn over it
     (filling 80% of the frame), so shoppers can line their face up the same
     way every time and the compare slider matches photo to photo.
   - Each card has an Edit button that reopens the editor on the UNCROPPED
     original (kept by the Worker), so a crop can be widened again later.
   - The date on each photo now comes from the photo's own EXIF
     DateTimeOriginal (when the camera took it), read before the canvas
     strips the metadata. Only the date tags are read. GPS and every other
     tag are ignored and then discarded by the re-encode. If there is no
     EXIF date (screenshots, PNG, WebP), the file's lastModified is used.
   - Photos are always sorted by that date, newest first.

   LSC - 23 September 2026, performance pass and photo limit:
   - The tab opens with ONE request. GET /photos now also returns
     `consented` and `limit`, which saves a whole app proxy round trip
     (about 220 ms measured). If the Worker is older and leaves `consented`
     out, the script falls back to GET /consent, so theme and Worker can be
     deployed in either order.
   - Each shopper may keep at most `limit` photos (6). A counter shows
     "3 of 6 photos". At the limit, Add photo is disabled with a note, and a
     picked file is refused BEFORE the crop editor opens, so nobody crops a
     photo only to have the upload rejected.

   LSC - 26 September 2026, own page and guest mode:
   - The tool moved out of the account page into its own page,
     sections/lsc-progress-photos.liquid on the page template
     page.lsc-progress-photos (/pages/progress-photos).
   - Signed-out visitors can crop and compare two photos. Those photos stay
     on their device only, in IndexedDB, and never reach the Worker. A card
     explains that a free account saves them, with Create account and Sign
     in buttons that bring the visitor back here.
   - Signed in, photos left on the device are saved to the account through
     the normal upload route, then removed from the device. It happens
     automatically if the visitor tapped Create account or Sign in on this
     page in the last 24 hours. Otherwise a card asks first, so someone else
     signing in on a shared device never inherits another person's photos
     silently. Device photos older than 7 days are deleted.
   - Buttons use lsc-progress__btn classes from lsc-progress.css, so the page
     no longer depends on the account page's stylesheet.

   LSC - 27 September 2026, rotation:
   - The crop editor has a Rotate slider under Zoom, from 45 degrees left to
     45 degrees right, so a tilted photo can be straightened to the outline.
     The outline stays upright and the photo turns underneath it.
   - The crop keeps its old shape { x, y, w, h } (the frame before turning,
     as fractions of the photo) and gains `r`, the angle in degrees,
     positive = clockwise. A crop without `r` is simply not rotated, so every
     photo saved before this change opens and renders exactly as it did.
   - While rotated, the frame is never allowed to reach past the photo's
     edge, so the saved image has no empty corners. Rotating further zooms
     in just enough to keep it filled, like a phone's photo editor, and
     rotating back zooms out again.

   LSC - 27 September 2026, sliders made for thumbs:
   - Zoom and Rotate each have a 44px button at both ends (zoom out / in,
     turn left / right). A tap moves one step, and holding the button keeps
     going, which is far easier than nudging a thin slider by a hair.
   - The sliders are taller (a 44px touch area), with a 28px thumb and a
     filled track. Rotate fills from the centre and has a tick at level.
   - On touch screens a slider jumps to wherever it is touched and follows
     the finger from there (touchSlider). iPhones otherwise only move a
     slider when the small thumb itself is grabbed. The same fix is on the
     before / after compare slider.

   Load-bearing details, none of them obvious:

   1. The browser resizes and re-encodes every photo before upload. Drawing an
      image into a canvas and exporting it drops the file's EXIF block, which
      is where a phone camera writes the GPS coordinates.

   2. createImageBitmap is called with imageOrientation "from-image". Without
      it, a photo that a phone marked as rotated is drawn sideways, because
      the canvas ignores the EXIF orientation flag that we are about to strip.

   3. Photos are shown with a plain <img src="/apps/.../photo/ID">. That URL
      goes through the app proxy, so the request is signed and authorised like
      any other. There is no presigned link and nothing to expire.
   ========================================================================= */

(function () {
  'use strict';

  /* ------------------------------------------------------------ sizes --- */

  /* The uncropped original is kept at this long edge, for re-cropping. */
  var ORIGINAL_EDGE = 1600;

  /* The cropped photo is 3:4 portrait, at most this wide (so 1200 x 1600). */
  var CROP_MAX_WIDTH = 1200;

  /* The grid image, also 3:4. */
  var THUMB_WIDTH = 300;

  /* The portrait frame. Width / height. Every cropped photo has this shape,
     which is what makes the compare slider line up. */
  var FRAME_RATIO = 3 / 4;

  /* How far the editor can zoom in, as a multiple of the widest crop. */
  var MAX_ZOOM = 4;

  /* LSC - How far the Rotate slider turns the photo either way, in degrees.
     45 is plenty to straighten a tilted selfie, and the maths that keeps the
     saved crop inside the photo (see openCropEditor) relies on it staying
     at or under 45. */
  var MAX_ROTATE = 45;

  /* LSC - Angles closer to level than this snap to exactly 0, so the thumb
     can be dropped back on "level" by hand on a phone. */
  var ROTATE_SNAP = 1;

  /* The editor works on a copy no larger than this, so a 48 megapixel phone
     photo does not exhaust memory on the phone that took it. */
  var EDITOR_SOURCE_EDGE = 2400;

  var JPEG_QUALITY = 0.85;

  /* LSC - Byte budgets, each a little under the Worker's own cap (1.5 MB
     crop, 2 MB original, 200 KB thumbnail, cut from 5 MB, 5 MB and 1 MB by
     the security fixes of 23 September 2026). An image that comes out over
     its budget is re-encoded at a lower JPEG quality until it fits, so a
     very detailed photo is saved slightly softer instead of being refused.
     Real photos are around 300 KB, 350 KB and 30 KB and never need it. */
  var MAX_CROP_BYTES = 1.4 * 1024 * 1024;
  var MAX_ORIGINAL_BYTES = 1.9 * 1024 * 1024;
  var MAX_THUMB_BYTES = 190 * 1024;

  /* LSC - The qualities tried, in order, when an image is over its budget.
     The first is the normal quality. */
  var FALLBACK_QUALITIES = [JPEG_QUALITY, 0.75, 0.65, 0.55, 0.45];

  /* LSC - Guest mode and device storage. See the device photo store below. */
  var GUEST_LIMIT_DEFAULT = 2;                   // photos a guest can hold
  var DEVICE_DB_NAME = 'lsc-progress-device';    // IndexedDB database
  var DEVICE_DB_STORE = 'photos';                // its one object store
  var DEVICE_MAX_AGE = 7 * 24 * 60 * 60 * 1000;  // delete device photos after 7 days
  var INTENT_KEY = 'lsc-progress:save-intent';   // localStorage: when Save was tapped
  var INTENT_MAX_AGE = 24 * 60 * 60 * 1000;      // auto-save window after that tap

  /* Words shown by the script. Kept together so they are easy to change. */
  var TEXT = {
    editorTitleNew: 'Crop your photo',
    editorTitleEdit: 'Edit crop',
    /* LSC - 27 September 2026: the hint and view label mention rotating. */
    /* LSC - 27 September 2026: shorter, so the photo gets more room on a
       phone, and it names the buttons beside the sliders. */
    editorHint: 'Line your face up with the outline so every photo matches. Drag to move and pinch to zoom. The buttons and sliders below zoom and straighten the photo.',
    viewLabel: 'Crop area. Drag, or use the arrow keys, to move the photo. Plus and minus zoom. Square brackets rotate.',
    zoom: 'Zoom',
    /* LSC - The zoom readout, one decimal place, e.g. "1.5×". */
    zoomShort: function (level) {
      return (Math.round(level * 10) / 10).toFixed(1) + '×';
    },
    /* LSC - The Rotate slider: its label, and how the angle is read out on
       screen (short) and to a screen reader (spoken). */
    rotate: 'Rotate',
    /* LSC - Spoken names of the step buttons beside the sliders. */
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    rotateLeft: 'Turn left 1 degree',
    rotateRight: 'Turn right 1 degree',
    rotateShort: function (deg) {
      return (deg > 0 ? '+' : '') + deg + '°';
    },
    rotateSpoken: function (deg) {
      if (!deg) return 'Level';
      return Math.abs(deg) + ' degrees ' + (deg > 0 ? 'right' : 'left');
    },
    cancel: 'Cancel',
    save: 'Save photo',
    edit: 'Edit',
    opening: 'Opening editor…',
    saving: 'Saving…',
    saved: 'Photo updated.',
    /* LSC - Guest and device-photo messages. */
    guestAdded: 'Photo added. It is on this device only. Add a second one to compare.',
    guestAddedCompare: 'Photo added. It is on this device only. Tap two photos to compare them.',
    guestFull: 'Create a free account to keep more.',
    guestLimit: function (n) {
      return 'Without an account you can compare ' + n + ' photos. Delete one, or create a free account to keep up to 6.';
    },
    deviceFound: function (n) {
      return n === 1
        ? 'You have 1 photo on this device from before you signed in. Save it to your account?'
        : 'You have ' + n + ' photos on this device from before you signed in. Save them to your account?';
    },
    deviceConsent: function (n) {
      return 'Agree to the photo terms to save the ' + (n === 1 ? 'photo' : n + ' photos') + ' you took before signing in.';
    },
    savingDevice: function (n) {
      return 'Saving ' + (n === 1 ? '1 photo' : n + ' photos') + ' to your account…';
    },
    savedDevice: function (n) {
      return 'Saved ' + (n === 1 ? '1 photo' : n + ' photos') + ' from this device to your account.';
    },
    accountFull: function (n) {
      return 'Your account is full, so ' + (n === 1 ? '1 photo stayed' : n + ' photos stayed') + ' on this device. Delete one to make room.';
    },
    discarded: 'Removed the photos from this device.'
  };

  /* ------------------------------------------------------------- helpers - */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text; // textContent, never innerHTML
    return node;
  }

  function svg(tag, attrs) {
    var node = document.createElementNS('http://www.w3.org/2000/svg', tag);
    Object.keys(attrs || {}).forEach(function (key) {
      node.setAttribute(key, attrs[key]);
    });
    return node;
  }

  /* LSC - Small line icons for the card buttons, drawn in currentColor so
     each follows its button's text colour. 24 x 24 grid, 2px stroke. */
  var ICONS = {
    // A pencil.
    edit: ['M4 20h4L19 9l-4-4L4 16z', 'M14 6l4 4'],
    // A lidded bin.
    trash: ['M4 7h16', 'M9 7V4h6v3', 'M6 7l1 13h10l1-13', 'M10 11v6', 'M14 11v6'],
    // A tick, shown while Delete waits for its second tap.
    confirm: ['M5 12.5l4.5 4.5L19 7.5'],
    // LSC - 27 September 2026: the crop editor's step buttons. A magnifier
    // with a minus or a plus, and a circular arrow turning each way.
    zoomOut: ['M10.5 17a6.5 6.5 0 1 0 0-13a6.5 6.5 0 1 0 0 13z', 'M20 20l-4.8-4.8', 'M7.5 10.5h6'],
    zoomIn: ['M10.5 17a6.5 6.5 0 1 0 0-13a6.5 6.5 0 1 0 0 13z', 'M20 20l-4.8-4.8', 'M7.5 10.5h6', 'M10.5 7.5v6'],
    rotateLeft: ['M4 4.5v5h5', 'M5.1 14.5a7.5 7.5 0 1 0 1.6-7.4L4 9.5'],
    rotateRight: ['M20 4.5v5h-5', 'M18.9 14.5a7.5 7.5 0 1 1-1.6-7.4L20 9.5']
  };

  function icon(name) {
    var root = svg('svg', {
      class: 'lsc-progress__icon',
      viewBox: '0 0 24 24',
      width: '20',
      height: '20',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '2',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
      focusable: 'false'
    });
    ICONS[name].forEach(function (d) {
      root.appendChild(svg('path', { d: d }));
    });
    return root;
  }

  function clamp(value, min, max) {
    return Math.min(max, Math.max(min, value));
  }

  /* LSC - 27 September 2026: make a range input easy to use with a finger.

     On an iPhone a slider only moves when its small thumb is grabbed;
     touching the track does nothing. Here, a touch or pen anywhere on the
     slider sets the value under the finger straight away, and the value
     keeps following the finger until it lifts. Mouse input is left to the
     browser, which already does this.

     thumbWidth is the thumb's width in CSS pixels. Browsers keep the
     thumb's centre half a thumb in from each end, so the same inset is used
     here and the value set matches where the thumb is drawn. The browser
     rounds the value to the slider's step. Fires `input` while moving and
     `change` when the finger lifts, like a real drag. */
  function touchSlider(input, thumbWidth) {
    var active = null;

    function setFromX(clientX) {
      var rect = input.getBoundingClientRect();
      var usable = rect.width - thumbWidth;
      if (usable <= 0) return;
      var fraction = clamp((clientX - rect.left - thumbWidth / 2) / usable, 0, 1);
      var min = Number(input.min) || 0;
      var max = Number(input.max);
      if (!Number.isFinite(max)) max = 100;
      var before = input.value;
      input.value = String(min + fraction * (max - min));
      if (input.value !== before) input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    input.addEventListener('pointerdown', function (event) {
      if (event.pointerType === 'mouse') return;
      active = event.pointerId;
      try {
        input.setPointerCapture(event.pointerId);
      } catch (error) {
        /* capture is a nicety: moves still arrive while over the slider */
      }
      setFromX(event.clientX);
    });

    input.addEventListener('pointermove', function (event) {
      if (event.pointerId !== active) return;
      event.preventDefault();
      setFromX(event.clientX);
    });

    function end(event) {
      if (event.pointerId !== active) return;
      active = null;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    }
    input.addEventListener('pointerup', end);
    input.addEventListener('pointercancel', end);
  }

  /* LSC - A button that repeats while held. One step on the first press,
     then, after a short pause, a step every 70 ms until the finger lifts or
     slides off. Keyboard presses (Enter, Space) arrive as a click with no
     pointer press before it and take one step each. Returns a function that stops any
     repeat, for when the editor closes mid-press. */
  function holdToRepeat(button, step) {
    var timer = null;
    /* True from a pointer press until its click arrives, so that click
       (which follows every tap) does not take a second step. */
    var pressed = false;
    function stop() {
      window.clearTimeout(timer);
      window.clearInterval(timer);
      timer = null;
    }
    button.addEventListener('pointerdown', function (event) {
      if (event.button) return; // main button or a touch only
      event.preventDefault(); // no text selection or focus flicker on long press
      pressed = true;
      stop();
      step();
      timer = window.setTimeout(function () {
        timer = window.setInterval(step, 70);
      }, 400);
    });
    ['pointerup', 'pointerleave', 'pointercancel', 'blur'].forEach(function (type) {
      button.addEventListener(type, stop);
    });
    button.addEventListener('click', function () {
      if (pressed) {
        pressed = false; // the tap already stepped on pointerdown
        return;
      }
      step(); // keyboard activation (Enter or Space)
    });
    button.addEventListener('keydown', function () {
      pressed = false; // a press that never became a click must not eat a key
    });
    // A long press on a phone must not open the copy / share menu.
    button.addEventListener('contextmenu', function (event) {
      event.preventDefault();
    });
    return stop;
  }

  /* LSC - Paint a crop editor slider's filled track. CSS draws the fill
     between --lsc-a and --lsc-b (fractions of the way along). Zoom fills
     from the left end. Rotate, marked with data-centre, fills from level
     (the middle) towards the thumb, so left and right read at a glance. */
  function paintFill(input) {
    var min = Number(input.min) || 0;
    var max = Number(input.max);
    var fraction = max > min ? clamp((Number(input.value) - min) / (max - min), 0, 1) : 0;
    var from = input.hasAttribute('data-centre') ? Math.min(0.5, fraction) : 0;
    var to = input.hasAttribute('data-centre') ? Math.max(0.5, fraction) : fraction;
    input.style.setProperty('--lsc-a', from.toFixed(4));
    input.style.setProperty('--lsc-b', to.toFixed(4));
  }

  function formatDate(seconds) {
    try {
      return new Date(seconds * 1000).toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return '';
    }
  }

  /* Newest first by the date the photo was taken, then by upload time. */
  function sortPhotos(list) {
    return list.sort(function (a, b) {
      return (b.taken_at - a.taken_at) || (b.created_at - a.created_at);
    });
  }

  /* One JPEG encode of a canvas at a given quality. */
  function encodeJpeg(canvas, quality) {
    return new Promise(function (resolve, reject) {
      canvas.toBlob(function (blob) {
        if (blob) resolve(blob);
        else reject(new Error('This image could not be read.'));
      }, 'image/jpeg', quality);
    });
  }

  /* LSC - Encode a canvas as JPEG no larger than maxBytes. Tries the normal
     quality first, then each lower one in FALLBACK_QUALITIES. If even the
     lowest is too big the photo is refused here, with a message, rather than
     uploaded and refused by the Worker. */
  async function canvasToBlob(canvas, maxBytes) {
    for (var i = 0; i < FALLBACK_QUALITIES.length; i++) {
      var blob = await encodeJpeg(canvas, FALLBACK_QUALITIES[i]);
      if (!maxBytes || blob.size <= maxBytes) return blob;
    }
    throw new Error('That photo is too large, even after resizing.');
  }

  function blobToBase64(blob) {
    return new Promise(function (resolve, reject) {
      var reader = new FileReader();
      reader.onerror = function () {
        reject(new Error('This image could not be read.'));
      };
      reader.onload = function () {
        /* The reader gives a data URL. Everything after the comma is the
           base64 payload the Worker expects. */
        var result = String(reader.result);
        resolve(result.slice(result.indexOf(',') + 1));
      };
      reader.readAsDataURL(blob);
    });
  }

  /* -------------------------------------------------------- EXIF date --- */

  /* LSC - Read the date the photo was taken from a JPEG's EXIF block.
     Returns unix seconds, or null. Reads ONLY DateTimeOriginal (0x9003),
     then DateTimeDigitized (0x9004), then DateTime (0x0132). No other tag is
     read. The GPS block is never touched here, and the canvas re-encode
     drops all EXIF before anything is uploaded.

     EXIF dates have no time zone. They are read as local time, which is what
     the phone showed when the photo was taken. */
  async function readExifDate(file) {
    try {
      if (!/jpe?g/i.test(file.type) && !/\.jpe?g$/i.test(file.name || '')) return null;

      // The EXIF block sits near the start. 256KB is plenty and cheap.
      var view = new DataView(await file.slice(0, 256 * 1024).arrayBuffer());
      if (view.byteLength < 4 || view.getUint16(0) !== 0xffd8) return null;

      // Walk the JPEG segments to APP1 "Exif\0\0".
      var offset = 2;
      while (offset + 4 <= view.byteLength) {
        var marker = view.getUint16(offset);
        if ((marker & 0xff00) !== 0xff00) return null;
        var length = view.getUint16(offset + 2);
        if (marker === 0xffe1 && offset + 10 <= view.byteLength && view.getUint32(offset + 4) === 0x45786966) {
          return parseTiffDate(view, offset + 10);
        }
        if (marker === 0xffda) return null; // image data starts, no EXIF
        offset += 2 + length;
      }
    } catch (error) {
      // Any malformed file simply has no usable date.
    }
    return null;
  }

  function parseTiffDate(view, start) {
    var little = view.getUint16(start) === 0x4949; // "II" = little endian
    function u16(p) { return view.getUint16(p, little); }
    function u32(p) { return view.getUint32(p, little); }
    if (u16(start + 2) !== 42) return null;

    // Read one IFD into { tag: entryOffset }.
    function readIfd(ifdOffset) {
      var entries = {};
      var at = start + ifdOffset;
      var count = u16(at);
      for (var i = 0; i < count; i++) {
        var entry = at + 2 + i * 12;
        entries[u16(entry)] = entry;
      }
      return entries;
    }

    // An ASCII tag longer than 4 bytes stores its value at an offset.
    function readAscii(entry) {
      if (entry == null) return null;
      var count = u32(entry + 4);
      var p = count > 4 ? start + u32(entry + 8) : entry + 8;
      var text = '';
      for (var i = 0; i < count - 1 && i < 32; i++) text += String.fromCharCode(view.getUint8(p + i));
      return text;
    }

    var ifd0 = readIfd(u32(start + 4));
    var exif = ifd0[0x8769] != null ? readIfd(u32(ifd0[0x8769] + 8)) : {};
    var text = readAscii(exif[0x9003]) || readAscii(exif[0x9004]) || readAscii(ifd0[0x0132]);

    // "YYYY:MM:DD HH:MM:SS"
    var m = text && text.match(/^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
    if (!m) return null;
    var date = new Date(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
    var seconds = Math.floor(date.getTime() / 1000);
    return Number.isFinite(seconds) && seconds > 946684800 ? seconds : null;
  }

  /* ------------------------------------------------------------ images --- */

  /* Decode a file or blob into a drawable source, upright, and no larger
     than maxEdge. Returns a canvas (which drawImage accepts like an image). */
  async function decodeImage(blob, maxEdge) {
    var bitmap = await createImageBitmap(blob, { imageOrientation: 'from-image' });
    var scale = Math.min(1, maxEdge / Math.max(bitmap.width, bitmap.height));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    if (bitmap.close) bitmap.close();
    return canvas;
  }

  /* The whole source, scaled to a long edge. Used for the stored original. */
  function renderWhole(source, maxEdge, maxBytes) {
    var scale = Math.min(1, maxEdge / Math.max(source.width, source.height));
    var canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(source.width * scale));
    canvas.height = Math.max(1, Math.round(source.height * scale));
    canvas.getContext('2d').drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvasToBlob(canvas, maxBytes); // LSC - kept under the Worker's cap
  }

  /* One crop of the source at a given output width, always 3:4. The crop is
     in fractions of the source, so it is the same whatever resolution the
     source happens to be. */
  function renderCrop(source, crop, maxWidth, maxBytes) {
    var sx = crop.x * source.width;
    var sy = crop.y * source.height;
    var sw = crop.w * source.width;
    var sh = crop.h * source.height;
    var width = Math.max(1, Math.round(Math.min(maxWidth, sw)));
    var height = Math.round(width / FRAME_RATIO);
    var canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    var context = canvas.getContext('2d');
    context.imageSmoothingQuality = 'high';
    /* LSC - 27 September 2026: a rotated crop is drawn through the same
       transform the editor uses, turned about the frame's centre. An
       unrotated crop keeps the plain copy below, so photos without `r` come
       out byte for byte as they did before rotation existed. */
    var angle = Number(crop.r) || 0;
    if (angle) {
      drawTurned(context, source, sx + sw / 2, sy + sh / 2, sw, angle, width, height);
    } else {
      context.drawImage(source, sx, sy, sw, sh, 0, 0, width, height);
    }
    return canvasToBlob(canvas, maxBytes); // LSC - kept under the Worker's cap
  }

  /* LSC - Draw a turned crop of `source` to fill a width x height canvas.

       (mx, my)  the crop's centre, in source pixels
       cw        the crop's width, in source pixels (its height follows the
                 3:4 frame, and width / height of the canvas is also 3:4)
       degrees   the turn, positive = clockwise on screen

     Read the transform from the bottom up: move the crop's centre to the
     origin, scale source pixels to canvas pixels, turn, then move the origin
     to the middle of the canvas. So the point at the crop's centre lands in
     the middle of the canvas, and the photo turns around it. The canvas
     clips everything that falls outside. Used by both the editor preview
     and the saved image, so what the shopper sees is what saves. */
  function drawTurned(context, source, mx, my, cw, degrees, width, height) {
    context.save();
    context.translate(width / 2, height / 2);
    context.rotate(degrees * Math.PI / 180);
    context.scale(width / cw, width / cw);
    context.translate(-mx, -my);
    context.drawImage(source, 0, 0);
    context.restore();
  }

  /* ------------------------------------------------------- crop editor --- */

  /* LSC - The portrait outline, in a 300 x 400 box that matches the 3:4
     frame. Each shape is drawn twice, dark and wide under light and thin, so
     it reads on both pale and dark photos. Purely a guide. It is never saved.

     LSC - 23 September 2026: the face oval is centred in the frame and fills
     80% of it (240 x 320 of 300 x 400), because the comparison is of faces,
     not shoulders. The shoulders were dropped. Two short neck lines under
     the chin keep the head upright. The eye line sits a little above the
     oval's middle, where eyes fall on a face, and a dotted centre line helps
     keep the nose straight.

     LSC - 24 September 2026: two eye outlines sit on the eye line, so the
     shopper can put their own eyes in the same place every time. Each is an
     almond 48 wide and about 13 tall with a small iris circle in the middle.
     A face is roughly five eye-widths across, so on the 240 wide oval each
     eye is 48 wide with one eye-width (48) between them: inner corners at
     x 126 and 174, outer corners at 78 and 222, centres at 102 and 198.

     LSC - 27 September 2026: the iris circles were removed (the eye
     outlines stay), and a mouth line was added so the lower face lines up
     too. On a face the base of the nose sits about halfway from the eyes
     (y 184) to the chin (y 360), so near y 272, and the mouth sits about a
     third of the way on from there to the chin, near y 300. Its corners
     line up with the middle of each eye (x 102 and 198), a little inside
     them at x 110 and 190, and it dips 3 units in the middle like relaxed
     lips. The dotted centre line now stops at y 284, above the mouth, so
     it does not cross it. */
  function buildOutline() {
    var root = svg('svg', {
      class: 'lsc-progress__editor-outline',
      viewBox: '0 0 300 400',
      preserveAspectRatio: 'none',
      'aria-hidden': 'true',
      focusable: 'false'
    });
    var shapes = [
      // Face: centred at (150, 200), 80% of the frame's width and height.
      ['ellipse', { cx: 150, cy: 200, rx: 120, ry: 160 }],
      // Neck hints, from under the chin to the bottom edge.
      ['path', { d: 'M112 352 C114 372 112 388 108 400' }],
      ['path', { d: 'M188 352 C186 372 188 388 192 400' }],
      // Eye line, about 45% of the way down the face. LSC - 24 September
      // 2026: now two short dashes outside the eyes (was one line from 60 to
      // 240) so it no longer runs through the eye outlines. They still show
      // the head is level.
      ['line', { x1: 42, y1: 184, x2: 70, y2: 184, 'stroke-dasharray': '6 6' }],
      ['line', { x1: 230, y1: 184, x2: 258, y2: 184, 'stroke-dasharray': '6 6' }],
      // LSC - Eyes: an almond for each eye (upper lid arcs a little higher
      // than the lower). LSC - 27 September 2026: the iris circles that sat
      // in the middle of each eye were removed.
      ['path', { d: 'M78 184 Q102 168 126 184 Q102 197 78 184 Z' }],
      ['path', { d: 'M174 184 Q198 168 222 184 Q198 197 174 184 Z' }],
      // LSC - 27 September 2026: mouth line, a gentle curve where the lips
      // meet. Lining it up keeps the chin and jaw at the same height in
      // every photo, which the eyes alone cannot do if the head tips.
      ['path', { d: 'M110 300 Q150 306 190 300' }],
      // Centre line, inside the oval. LSC - now ends at y 284 (was 340) so
      // it stops above the mouth line instead of running through it.
      ['line', { x1: 150, y1: 60, x2: 150, y2: 284, 'stroke-dasharray': '4 8' }]
    ];
    ['lsc-progress__outline-shadow', 'lsc-progress__outline-line'].forEach(function (layer) {
      var group = svg('g', { class: layer });
      shapes.forEach(function (shape) {
        group.appendChild(svg(shape[0], shape[1]));
      });
      root.appendChild(group);
    });
    return root;
  }

  /* Open the editor over `source` (a canvas). `crop` is where to start, as
     fractions, or null to start centred and as wide as the frame allows.
     Resolves { crop } when saved, or null when cancelled. */
  function openCropEditor(source, crop, title) {
    return new Promise(function (resolve) {
      var srcW = source.width;
      var srcH = source.height;

      /* State is kept in SOURCE pixels, so resizing the dialog never moves
         the crop. cw is the crop width. The height always follows the frame
         shape.

         LSC - 27 September 2026, rotation: the crop is now held by its
         CENTRE (mx, my) rather than its top-left corner, plus `angle` in
         degrees. Turning happens about the centre, so the centre is the one
         point that does not move when the Rotate slider does. The old
         top-left form is worked out again only when saving. */
      var cw, mx, my;
      var angle = 0;

      function ch() {
        return cw / FRAME_RATIO;
      }

      /* LSC - The widest crop that still fits inside the photo at a given
         angle. A turned frame of width w and height w / FRAME_RATIO takes up
         a box w * (|cos| + |sin| / FRAME_RATIO) wide and
         w * (|sin| + |cos| / FRAME_RATIO) tall, and that box has to fit in
         the photo. At 0 degrees this is the old min(srcW, srcH * ratio). */
      function maxCwFor(degrees) {
        var t = degrees * Math.PI / 180;
        var c = Math.abs(Math.cos(t));
        var s = Math.abs(Math.sin(t));
        return Math.min(srcW / (c + s / FRAME_RATIO), srcH / (s + c / FRAME_RATIO));
      }

      /* The widest crop now, and the narrowest (fully zoomed in). */
      function maxCw() {
        return maxCwFor(angle);
      }
      function minCw() {
        return maxCw() / MAX_ZOOM;
      }

      /* LSC - Keep the frame inside the photo. The turned frame's bounding
         box reaches hx either side of the centre and hy above and below, so
         the centre may go no nearer an edge than that. With no turn, hx and
         hy are half the frame, which is the old rule. */
      function constrain() {
        cw = clamp(cw, minCw(), maxCw());
        var t = angle * Math.PI / 180;
        var c = Math.abs(Math.cos(t));
        var s = Math.abs(Math.sin(t));
        var hx = (cw / 2) * c + (ch() / 2) * s;
        var hy = (cw / 2) * s + (ch() / 2) * c;
        mx = clamp(mx, hx, srcW - hx);
        my = clamp(my, hy, srcH - hy);
      }

      /* LSC - Turn a distance on screen into a distance in the photo. The
         photo is drawn turned by `angle`, so a drag has to be turned back
         the other way (by -angle) before it is applied to the centre. */
      function unturn(dx, dy) {
        var t = angle * Math.PI / 180;
        var c = Math.cos(t);
        var s = Math.sin(t);
        return { x: dx * c + dy * s, y: -dx * s + dy * c };
      }

      if (crop && crop.w > 0) {
        cw = crop.w * srcW;
        // LSC - Stored as the top-left of the unturned frame, so step in to
        // the centre. A crop saved before rotation has no `r`: level.
        mx = crop.x * srcW + cw / 2;
        my = crop.y * srcH + ch() / 2;
        angle = clamp(Number(crop.r) || 0, -MAX_ROTATE, MAX_ROTATE);
      } else {
        cw = maxCw();
        mx = srcW / 2;
        // Start a little above centre, where a face usually is.
        my = Math.max(0, (srcH - cw / FRAME_RATIO) * 0.35) + ch() / 2;
      }
      constrain();

      /* ------------------------------------------------ dialog markup -- */

      var dialog = el('dialog', 'lsc-progress__editor');
      dialog.setAttribute('aria-labelledby', 'LscCropTitle');

      var heading = el('h2', 'lsc-progress__editor-title', title);
      heading.id = 'LscCropTitle';
      dialog.appendChild(heading);
      dialog.appendChild(el('p', 'lsc-progress__editor-hint', TEXT.editorHint));

      var view = el('div', 'lsc-progress__editor-view');
      view.tabIndex = 0;
      view.setAttribute('role', 'group');
      view.setAttribute('aria-label', TEXT.viewLabel);
      var canvas = el('canvas', 'lsc-progress__editor-canvas');
      view.appendChild(canvas);
      view.appendChild(buildOutline());
      dialog.appendChild(view);

      /* LSC - 27 September 2026: one slider row, laid out as

           [label]  [step down]  ====o====  [step up]  [readout]

         The row is a <div>, not a <label>: a label wrapping buttons would
         pass every tap on the words to the first button. The slider is
         named by aria-labelledby instead. The words are hidden on narrow
         phones (CSS), where the icons on the buttons carry the meaning and
         the slider needs the width. The readout ("1.5×", "+3°") is for
         sighted users; screen readers get aria-valuetext. */
      var rowCount = 0;
      function sliderRow(options) {
        rowCount += 1;
        var row = el('div', 'lsc-progress__editor-zoom' + (options.extraClass ? ' ' + options.extraClass : ''));
        var name = el('span', 'lsc-progress__editor-label', options.label);
        name.id = 'LscCropSlider' + rowCount;
        row.appendChild(name);

        var less = el('button', 'lsc-progress__editor-step');
        less.type = 'button';
        less.setAttribute('aria-label', options.lessLabel);
        less.appendChild(icon(options.lessIcon));
        row.appendChild(less);

        var input = el('input', 'lsc-progress__editor-range');
        input.type = 'range';
        input.min = options.min;
        input.max = options.max;
        input.step = options.step;
        input.setAttribute('aria-labelledby', name.id);
        if (options.centre) input.setAttribute('data-centre', '');
        row.appendChild(input);

        var more = el('button', 'lsc-progress__editor-step');
        more.type = 'button';
        more.setAttribute('aria-label', options.moreLabel);
        more.appendChild(icon(options.moreIcon));
        row.appendChild(more);

        var value = el('span', 'lsc-progress__editor-value');
        value.setAttribute('aria-hidden', 'true');
        row.appendChild(value);

        dialog.appendChild(row);
        return { row: row, input: input, less: less, more: more, value: value };
      }

      var zoomParts = sliderRow({
        label: TEXT.zoom,
        min: '1',
        max: String(MAX_ZOOM),
        step: '0.01',
        lessLabel: TEXT.zoomOut,
        lessIcon: 'zoomOut',
        moreLabel: TEXT.zoomIn,
        moreIcon: 'zoomIn'
      });
      var zoom = zoomParts.input;
      var zoomValue = zoomParts.value;

      /* LSC - 27 September 2026: the Rotate row. Left of centre turns the
         photo left (anticlockwise), right of centre turns it right. The
         readout on the end shows the angle, and aria-valuetext says it in
         words, e.g. "3 degrees left", instead of a bare "-3". */
      var rotateParts = sliderRow({
        extraClass: 'lsc-progress__editor-rotate',
        label: TEXT.rotate,
        min: String(-MAX_ROTATE),
        max: String(MAX_ROTATE),
        step: '0.5',
        centre: true,
        lessLabel: TEXT.rotateLeft,
        lessIcon: 'rotateLeft',
        moreLabel: TEXT.rotateRight,
        moreIcon: 'rotateRight'
      });
      var rotateRow = rotateParts.row;
      var rotate = rotateParts.input;
      var rotateValue = rotateParts.value;

      var actions = el('div', 'lsc-progress__editor-actions');
      // LSC - lsc-progress__btn, so the editor needs no account stylesheet.
      var cancel = el('button', 'lsc-progress__btn lsc-progress__btn--secondary', TEXT.cancel);
      cancel.type = 'button';
      var save = el('button', 'lsc-progress__btn lsc-progress__btn--primary', TEXT.save);
      save.type = 'button';
      actions.appendChild(cancel);
      actions.appendChild(save);
      dialog.appendChild(actions);

      /* --------------------------------------------------- drawing ----- */

      var frame = 0;
      function draw() {
        frame = 0;
        var rect = view.getBoundingClientRect();
        var dpr = window.devicePixelRatio || 1;
        var width = Math.max(1, Math.round(rect.width * dpr));
        var height = Math.max(1, Math.round(rect.height * dpr));
        if (canvas.width !== width) canvas.width = width;
        if (canvas.height !== height) canvas.height = height;
        var context = canvas.getContext('2d');
        context.imageSmoothingQuality = 'high';
        context.clearRect(0, 0, width, height);
        // LSC - Drawn through the same turn-aware helper that renders the
        // saved image, so the preview and the result always agree.
        drawTurned(context, source, mx, my, cw, angle, width, height);
        zoom.value = String(maxCw() / cw);
        zoomValue.textContent = TEXT.zoomShort(maxCw() / cw);
        paintFill(zoom); // LSC - the filled part of the track
        showAngle();
      }

      /* LSC - Put the current angle on the Rotate slider and its readout.
         Skipped while the slider is being dragged, so the snap to level
         does not fight the thumb under the shopper's finger. */
      function showAngle() {
        if (!rotating) rotate.value = String(angle);
        paintFill(rotate); // LSC - follows the thumb, even mid-drag
        rotateValue.textContent = TEXT.rotateShort(angle);
        rotate.setAttribute('aria-valuetext', TEXT.rotateSpoken(angle));
      }

      function redraw() {
        constrain();
        if (!frame) frame = window.requestAnimationFrame(draw);
      }

      /* Zoom to a new crop width, keeping the point (fx, fy) still. The
         point is a fraction of the view, 0.5 / 0.5 being the centre. */
      function zoomTo(newCw, fx, fy) {
        /* LSC - Rotation-aware. The offset from the frame's centre to the
           focus point is measured along the turned frame, then turned back
           into photo pixels. Keeping the photo point under the focus fixed
           gives the new centre. With no turn this is the old maths. */
        var ox = (fx == null ? 0.5 : fx) - 0.5;
        var oy = (fy == null ? 0.5 : fy) - 0.5;
        var before = unturn(ox * cw, oy * ch());
        cw = clamp(newCw, minCw(), maxCw());
        var after = unturn(ox * cw, oy * ch());
        mx += before.x - after.x;
        my += before.y - after.y;
        redraw();
      }

      /* Move by a distance in screen pixels. LSC - turned back by the
         angle first, so the photo follows the finger at any rotation. */
      function panBy(dx, dy) {
        var perPixel = cw / view.getBoundingClientRect().width;
        var d = unturn(dx * perPixel, dy * perPixel);
        mx -= d.x;
        my -= d.y;
        redraw();
      }

      /* LSC - Turn to a new angle about the frame's centre. The zoom level
         (widest crop / crop) is kept, so turning shrinks the frame only as
         much as needed to stay inside the photo, and turning back grows it
         again. Without this, turning to 20 degrees and back would leave the
         photo zoomed in. Angles within ROTATE_SNAP of level become 0. */
      function rotateTo(degrees) {
        var level = maxCw() / cw;
        degrees = clamp(Number(degrees) || 0, -MAX_ROTATE, MAX_ROTATE);
        if (Math.abs(degrees) < ROTATE_SNAP) degrees = 0;
        angle = Math.round(degrees * 2) / 2; // half-degree steps, like the slider
        cw = maxCw() / level;
        redraw();
      }

      /* ----------------------------------------------- pointer input --- */

      /* One pointer drags. Two pointers pinch and drag together. Pointer
         events cover mouse, touch and pen with one code path. */
      var pointers = new Map();
      var pinch = null;

      function pinchState() {
        var points = Array.from(pointers.values());
        var rect = view.getBoundingClientRect();
        return {
          distance: Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y),
          midX: (points[0].x + points[1].x) / 2,
          midY: (points[0].y + points[1].y) / 2,
          fx: ((points[0].x + points[1].x) / 2 - rect.left) / rect.width,
          fy: ((points[0].y + points[1].y) / 2 - rect.top) / rect.height
        };
      }

      view.addEventListener('pointerdown', function (event) {
        view.setPointerCapture(event.pointerId);
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 2) pinch = pinchState();
        view.classList.add('lsc-progress__editor-view--dragging');
      });

      view.addEventListener('pointermove', function (event) {
        var last = pointers.get(event.pointerId);
        if (!last) return;
        if (pointers.size === 1) {
          panBy(event.clientX - last.x, event.clientY - last.y);
          pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
          return;
        }
        pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
        if (pointers.size === 2 && pinch) {
          var next = pinchState();
          if (next.distance > 0 && pinch.distance > 0) {
            zoomTo(cw * (pinch.distance / next.distance), next.fx, next.fy);
          }
          panBy(next.midX - pinch.midX, next.midY - pinch.midY);
          pinch = next;
        }
      });

      function endPointer(event) {
        pointers.delete(event.pointerId);
        pinch = pointers.size === 2 ? pinchState() : null;
        if (!pointers.size) view.classList.remove('lsc-progress__editor-view--dragging');
      }
      view.addEventListener('pointerup', endPointer);
      view.addEventListener('pointercancel', endPointer);

      /* Mouse wheel and trackpad pinch (which arrives as ctrl+wheel). */
      view.addEventListener('wheel', function (event) {
        event.preventDefault();
        var rect = view.getBoundingClientRect();
        var factor = Math.exp(event.deltaY * (event.ctrlKey ? 0.01 : 0.0015));
        zoomTo(cw * factor, (event.clientX - rect.left) / rect.width, (event.clientY - rect.top) / rect.height);
      }, { passive: false });

      /* ---------------------------------------------- keyboard input --- */

      view.addEventListener('keydown', function (event) {
        var step = (event.shiftKey ? 40 : 8);
        var handled = true;
        switch (event.key) {
          case 'ArrowLeft': panBy(step, 0); break;
          case 'ArrowRight': panBy(-step, 0); break;
          case 'ArrowUp': panBy(0, step); break;
          case 'ArrowDown': panBy(0, -step); break;
          case '+':
          case '=': zoomTo(cw / 1.1); break;
          case '-':
          case '_': zoomTo(cw * 1.1); break;
          // LSC - Square brackets turn left and right, 1 degree a press
          // (5 with Shift). The Rotate slider's own arrow keys also work.
          case '[':
          case '{': rotateTo(angle - (event.shiftKey ? 5 : 1)); break;
          case ']':
          case '}': rotateTo(angle + (event.shiftKey ? 5 : 1)); break;
          default: handled = false;
        }
        if (handled) event.preventDefault();
      });

      zoom.addEventListener('input', function () {
        zoomTo(maxCw() / Number(zoom.value));
      });

      /* LSC - The Rotate slider. `rotating` is true while a pointer holds
         the thumb, so showAngle leaves the thumb where the finger put it
         (the photo still snaps to level). On release the thumb catches up
         with the snapped angle. */
      var rotating = false;
      rotate.addEventListener('pointerdown', function () {
        rotating = true;
      });
      ['pointerup', 'pointercancel', 'change', 'blur'].forEach(function (type) {
        rotate.addEventListener(type, function () {
          rotating = false;
          showAngle();
        });
      });
      rotate.addEventListener('input', function () {
        rotateTo(rotate.value);
      });

      /* LSC - Finger-friendly sliders (see touchSlider). 28 is the thumb
         width set in lsc-progress.css. */
      touchSlider(zoom, 28);
      touchSlider(rotate, 28);

      /* LSC - The step buttons. Zoom moves by 10% a step, the same as the
         + and - keys. Rotate moves by 1 degree, the same as [ and ]. Each
         repeats while held. The stop functions are kept so closing the
         editor mid-press cannot leave a repeat running. */
      var stopRepeats = [
        holdToRepeat(zoomParts.less, function () {
          zoomTo(cw * 1.1);
        }),
        holdToRepeat(zoomParts.more, function () {
          zoomTo(cw / 1.1);
        }),
        holdToRepeat(rotateParts.less, function () {
          rotateTo(angle - 1);
        }),
        holdToRepeat(rotateParts.more, function () {
          rotateTo(angle + 1);
        })
      ];

      /* LSC - Double-click or double-tap the Rotate label or readout (the
         readout alone on phones, where the label is hidden) to go back to
         level. A small convenience; the slider alone can get there too,
         thanks to the snap. */
      rotateRow.addEventListener('dblclick', function (event) {
        // LSC - Only on the words or the readout. Two quick taps on the
        // slider or the step buttons are nudges and must not reset.
        if (!event.target.closest('.lsc-progress__editor-label, .lsc-progress__editor-value')) return;
        event.preventDefault();
        rotateTo(0);
        showAngle();
      });

      /* ------------------------------------------------ open / close --- */

      var observer = window.ResizeObserver ? new ResizeObserver(redraw) : null;
      var settled = false;

      /* LSC - 27 September 2026: size the photo to the room left on screen.
         With the Rotate row added, the editor was about 20px too tall for
         an iPhone SE with Safari's bars showing, so Save needed a scroll.
         This measures everything in the dialog except the photo (title,
         hint, both sliders, buttons, gaps and padding), and gives the photo
         whatever is left of 94% of the window, never more than the CSS
         limit of 56% and never under 160px. The width follows at 3:4. On
         screens tall enough for the CSS limit this changes nothing. */
      function fitView() {
        var styles = window.getComputedStyle(dialog);
        var gap = parseFloat(styles.rowGap) || 0;
        var used = (parseFloat(styles.paddingTop) || 0) + (parseFloat(styles.paddingBottom) || 0);
        Array.prototype.forEach.call(dialog.children, function (child, index) {
          if (index) used += gap;
          if (child !== view) used += child.getBoundingClientRect().height;
        });
        var height = window.innerHeight;
        var room = Math.max(160, Math.min(height * 0.56, Math.floor(height * 0.94 - used - 1)));
        view.style.maxHeight = room + 'px';
        view.style.width = 'min(100%, ' + Math.floor(room * FRAME_RATIO) + 'px)';
      }

      function finish(result) {
        if (settled) return;
        settled = true;
        if (observer) observer.disconnect();
        stopRepeats.forEach(function (stop) {
          stop(); // LSC - no step-button repeat outlives the editor
        });
        window.removeEventListener('resize', fitView); // LSC
        window.removeEventListener('resize', redraw); // LSC - the fallback below
        if (dialog.open) dialog.close();
        dialog.remove();
        resolve(result);
      }

      cancel.addEventListener('click', function () {
        finish(null);
      });

      // Escape closes a modal dialog. Treat it as Cancel.
      dialog.addEventListener('cancel', function (event) {
        event.preventDefault();
        finish(null);
      });

      save.addEventListener('click', function () {
        constrain();
        /* LSC - Saved as the top-left of the UNTURNED frame plus `r`, so the
           shape is the one the Worker and older code already accept. For
           turns up to 45 degrees that unturned frame always sits inside the
           photo (the turned one does, and it is the larger box), so x, y,
           w and h stay between 0 and 1. `r` is left off at level, so a
           level crop is stored exactly as before. */
        var result = {
          x: (mx - cw / 2) / srcW,
          y: (my - ch() / 2) / srcH,
          w: cw / srcW,
          h: ch() / srcH
        };
        if (angle) result.r = angle;
        finish({ crop: result });
      });

      // Appended to <body> so no theme wrapper (transforms, overflow, the
      // .section--padding ::before) can affect it. showModal puts it in the
      // top layer and makes the rest of the page inert.
      document.body.appendChild(dialog);
      dialog.showModal();
      fitView(); // LSC - needs the dialog open, so its rows have real sizes
      window.addEventListener('resize', fitView); // LSC - e.g. turning the phone
      if (observer) observer.observe(view);
      else window.addEventListener('resize', redraw);
      redraw();
      view.focus();
    });
  }

  /* ------------------------------------------------- device photo store -- */

  /* LSC - 26 September 2026: photos taken while signed out live only on the
     shopper's own device, in IndexedDB. Nothing is sent to the Worker or to
     Shopify, so no account, consent or server storage is involved.

     Why IndexedDB and not localStorage: each record holds three JPEG blobs
     (about 700 KB together), far beyond localStorage's 5 MB string budget,
     and IndexedDB stores Blobs as they are, with no base64 inflation.

     The store survives the trip to the Create account / Sign in page and
     back, which is what lets the photos be saved to the new account without
     uploading them again.

     If IndexedDB is unavailable (some private browsing modes), the store
     quietly falls back to memory: guests can still crop and compare, the
     photos are simply gone after a reload. */
  function openDeviceDb() {
    return new Promise(function (resolve) {
      try {
        if (!window.indexedDB) {
          resolve(null);
          return;
        }
        var request = window.indexedDB.open(DEVICE_DB_NAME, 1);
        request.onupgradeneeded = function () {
          request.result.createObjectStore(DEVICE_DB_STORE, { keyPath: 'id' });
        };
        request.onsuccess = function () {
          resolve(request.result);
        };
        request.onerror = function () {
          resolve(null);
        };
        request.onblocked = function () {
          resolve(null);
        };
      } catch (error) {
        resolve(null);
      }
    });
  }

  function createDeviceStore() {
    var memory = new Map();
    var dbPromise = null;

    function database() {
      if (!dbPromise) dbPromise = openDeviceDb();
      return dbPromise;
    }

    /* Run one request in its own transaction. Resolves with the request's
       result once the transaction commits, or null when there is no
       database, so callers can fall back to memory. */
    function run(mode, makeRequest) {
      return database().then(function (db) {
        if (!db) return null;
        return new Promise(function (resolve, reject) {
          var transaction = db.transaction(DEVICE_DB_STORE, mode);
          var request = makeRequest(transaction.objectStore(DEVICE_DB_STORE));
          var result = true;
          request.onsuccess = function () {
            if (request.result !== undefined) result = request.result;
          };
          transaction.oncomplete = function () {
            resolve(result);
          };
          transaction.onerror = transaction.onabort = function () {
            reject(transaction.error || new Error('This device could not store the photo.'));
          };
        });
      });
    }

    return {
      all: async function () {
        try {
          var rows = await run('readonly', function (store) {
            return store.getAll();
          });
          if (Array.isArray(rows)) return rows;
        } catch (error) {
          /* fall through to memory */
        }
        return Array.from(memory.values());
      },
      put: async function (record) {
        memory.set(record.id, record);
        try {
          await run('readwrite', function (store) {
            return store.put(record);
          });
        } catch (error) {
          /* kept in memory for this visit */
        }
      },
      remove: async function (id) {
        memory.delete(id);
        try {
          await run('readwrite', function (store) {
            return store.delete(id);
          });
        } catch (error) {
          /* nothing else to do */
        }
      },
      clear: async function () {
        memory.clear();
        try {
          await run('readwrite', function (store) {
            return store.clear();
          });
        } catch (error) {
          /* nothing else to do */
        }
      }
    };
  }

  /* LSC - "Save intent": set when a guest taps Create account or Sign in on
     this page. When they come back signed in within INTENT_MAX_AGE, their
     device photos are saved to the account automatically. Without a recent
     intent the shopper is asked first, so a different person signing in on a
     shared device is never handed someone else's photos silently. Only a
     timestamp is stored, never anything about the shopper. */
  function markSaveIntent() {
    try {
      window.localStorage.setItem(INTENT_KEY, String(Date.now()));
    } catch (error) {
      /* storage blocked: the shopper will simply be asked instead */
    }
  }

  function hasRecentIntent() {
    try {
      var at = Number(window.localStorage.getItem(INTENT_KEY));
      return Boolean(at) && Date.now() - at < INTENT_MAX_AGE;
    } catch (error) {
      return false;
    }
  }

  function clearSaveIntent() {
    try {
      window.localStorage.removeItem(INTENT_KEY);
    } catch (error) {
      /* nothing to clear */
    }
  }

  /* A random id for a device photo. Same shape as the Worker's ids. */
  function newLocalId() {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID().replace(/-/g, '');
    return String(Date.now()) + Math.random().toString(16).slice(2);
  }

  /* ------------------------------------------------------------ the page -- */

  /* LSC - 26 September 2026: this used to be a tab inside the account page.
     It now drives the standalone page from sections/lsc-progress-photos.liquid
     (and would still drive any other markup with data-progress-panel).

     Two modes, chosen by Liquid through data-progress-guest:
       signed in  photos are stored by the Worker, up to `limit` (6)
       guest      photos stay on this device only, up to the guest limit (2),
                  enough to crop two photos and compare them */
  function initProgress(panel) {
    if (!panel || panel.hasAttribute('data-progress-ready')) return;
    panel.setAttribute('data-progress-ready', '');

    var guest = panel.getAttribute('data-progress-guest') === 'true';
    var endpoint = (panel.getAttribute('data-progress-endpoint') || '').replace(/\/$/, '');
    if (!guest && !endpoint) return;

    var grid = panel.querySelector('[data-progress-grid]');
    var emptyNote = panel.querySelector('[data-progress-empty]');
    var status = panel.querySelector('[data-progress-status]');
    var input = panel.querySelector('[data-progress-input]');
    var addLabel = panel.querySelector('[data-progress-add]');
    var consentBox = panel.querySelector('[data-progress-consent]');
    var consentAccept = panel.querySelector('[data-progress-consent-accept]');
    var consentCancel = panel.querySelector('[data-progress-consent-cancel]');
    var compareButton = panel.querySelector('[data-progress-compare-open]');
    var comparePanel = panel.querySelector('[data-progress-compare]');
    var listSection = panel.querySelector('[data-progress-list]');

    /* LSC - The "photos on this device" card, signed-in mode only. */
    var deviceCard = panel.querySelector('[data-progress-device]');
    var deviceText = panel.querySelector('[data-progress-device-text]');
    var deviceSave = panel.querySelector('[data-progress-device-save]');
    var deviceDiscard = panel.querySelector('[data-progress-device-discard]');

    var device = createDeviceStore();

    var photos = [];
    var selected = [];
    /* Guests never send anything, so there is nothing to consent to. */
    var consented = guest;
    /* Set while the consent card is open because an upload is waiting on it. */
    var pendingFile = null;
    /* LSC - Set while the consent card is open because device photos are
       waiting to be saved to the account. */
    var pendingDevice = false;
    /* True while the editor or an upload is running, so a second file pick
       cannot start a second editor on top of the first. */
    var busy = false;
    /* LSC - The photo limit. Signed in: the Worker sends the real value with
       the list, and 6 only applies until it arrives. Guest: from the page. */
    var limit = guest
      ? Number(panel.getAttribute('data-progress-guest-limit')) || GUEST_LIMIT_DEFAULT
      : 6;

    /* LSC - "3 of 6 photos", placed just above the status line. */
    var counter = el('p', 'lsc-progress__count');
    counter.setAttribute('aria-live', 'polite');
    if (status && status.parentNode) status.parentNode.insertBefore(counter, status);

    function atLimit() {
      return photos.length >= limit;
    }

    function limitMessage() {
      return guest ? TEXT.guestLimit(limit) : 'You can keep up to ' + limit + ' photos. Delete one to add a new one.';
    }

    /* LSC - Disable or enable Add photo to match the limit. The label drives
       the hidden file input, so disabling the input disables the button
       too. aria-disabled and a class carry the look and the announcement. */
    function syncAddButton() {
      var full = atLimit();
      if (input) input.disabled = full;
      if (addLabel) {
        addLabel.classList.toggle('lsc-progress__file-label--disabled', full);
        if (full) addLabel.setAttribute('aria-disabled', 'true');
        else addLabel.removeAttribute('aria-disabled');
        addLabel.title = full ? limitMessage() : '';
      }
      if (!photos.length) {
        counter.textContent = '';
      } else if (guest) {
        counter.textContent = photos.length + ' of ' + limit + ' photos on this device' +
          (full ? '. ' + TEXT.guestFull : '');
      } else {
        counter.textContent = photos.length + ' of ' + limit + ' photos' +
          (full ? '. Delete one to add a new one.' : '');
      }
    }

    function say(message, isError) {
      if (!status) return;
      status.textContent = message || '';
      status.classList.toggle('lsc-progress__status--error', Boolean(isError));
    }

    /* ------------------------------------------------ device photos ---- */

    /* LSC - Turn a device record into the same shape as a Worker photo, with
       object URLs standing in for the proxy URLs. */
    function localPhoto(record) {
      return {
        id: record.id,
        taken_at: record.taken_at,
        created_at: record.created_at,
        crop: record.crop || null,
        local: true,
        record: record,
        urls: {
          thumb: URL.createObjectURL(record.thumb),
          photo: URL.createObjectURL(record.full),
          original: URL.createObjectURL(record.original || record.full)
        }
      };
    }

    /* Object URLs hold their blob in memory until revoked. */
    function releaseUrls(photo) {
      if (!photo || !photo.urls) return;
      Object.keys(photo.urls).forEach(function (key) {
        URL.revokeObjectURL(photo.urls[key]);
      });
    }

    /* Device records, oldest first, after dropping any past DEVICE_MAX_AGE.
       Old photos are removed rather than kept forever on a shared device. */
    async function deviceRecords() {
      var records = await device.all();
      var cutoff = Date.now() - DEVICE_MAX_AGE;
      var keep = [];
      for (var i = 0; i < records.length; i++) {
        if ((records[i].stored_at || 0) < cutoff) await device.remove(records[i].id);
        else keep.push(records[i]);
      }
      return keep.sort(function (a, b) {
        return (a.taken_at - b.taken_at) || (a.stored_at - b.stored_at);
      });
    }

    function showDeviceCard(count) {
      if (!deviceCard) return;
      if (deviceText) deviceText.textContent = TEXT.deviceFound(count);
      deviceCard.hidden = false;
    }

    function hideDeviceCard() {
      if (deviceCard) deviceCard.hidden = true;
    }

    /* LSC - Signed in: save the photos taken on this device while signed out
       to the account, then remove them from the device. Uploads one at a
       time through the normal upload route, so the Worker checks each one
       exactly as it checks any upload. Stops at the account limit. */
    var savingDevice = false;
    async function saveDevicePhotos() {
      if (savingDevice) return;
      savingDevice = true;
      busy = true;
      hideDeviceCard();

      var records = await deviceRecords();
      var room = Math.max(0, limit - photos.length);
      var toSave = records.slice(0, room);
      var saved = 0;
      var failed = null;

      say(TEXT.savingDevice(toSave.length));
      try {
        for (var i = 0; i < toSave.length; i++) {
          var record = toSave[i];
          var body = {
            data: await blobToBase64(record.full),
            thumb: await blobToBase64(record.thumb),
            crop: record.crop || undefined,
            taken_at: record.taken_at
          };
          if (record.original) body.original = await blobToBase64(record.original);
          var result = await postJson('/photos', body);
          photos.push(result.photo);
          await device.remove(record.id);
          saved += 1;
        }
      } catch (error) {
        failed = error;
      } finally {
        busy = false;
        savingDevice = false;
      }

      sortPhotos(photos);
      render();
      if (saved) foldTips(); // LSC - photos now come first, as on load

      var left = records.length - saved;
      if (left === 0) clearSaveIntent();

      if (failed) {
        if (failed.code === 'consent_required') {
          consented = false;
          pendingDevice = true;
          showConsent(true);
          say('');
          return;
        }
        say(failed.message, true);
        if (left > 0) showDeviceCard(left);
        return;
      }

      var message = saved ? TEXT.savedDevice(saved) : '';
      if (left > 0) message += (message ? ' ' : '') + TEXT.accountFull(left);
      say(message, !saved && left > 0);
    }

    /* LSC - Signed in, on load: offer or perform the save. With a recent save
       intent it happens without a question (after consent, if the shopper
       has not agreed yet). Otherwise a card asks first. */
    async function offerDevicePhotos() {
      var records = await deviceRecords();
      if (!records.length) return;

      if (hasRecentIntent()) {
        if (!consented) {
          pendingDevice = true;
          showConsent(true);
          say(TEXT.deviceConsent(records.length));
          return;
        }
        await saveDevicePhotos();
        return;
      }

      showDeviceCard(records.length);
    }

    /* ------------------------------------------------------- requests -- */

    /* An image URL. Signed in: through the proxy, where `_v` changes after an
       edit so the browser fetches the new crop. Guest: the object URL. */
    function imageUrl(kind, photo) {
      if (photo.local) return photo.urls[kind];
      return endpoint + '/' + kind + '/' + encodeURIComponent(photo.id) + (photo._v ? '?v=' + photo._v : '');
    }

    async function api(path, options) {
      var response = await fetch(endpoint + path, options || {});
      var payload = null;
      try {
        payload = await response.json();
      } catch (error) {
        payload = null;
      }
      if (!response.ok) {
        var error = new Error((payload && payload.error) || 'Something went wrong.');
        error.status = response.status;
        error.code = payload && payload.code;
        throw error;
      }
      return payload;
    }

    function postJson(path, body) {
      return api(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body || {})
      });
    }

    /* ------------------------------------------------------- consent ---- */

    function showConsent(show) {
      if (consentBox) consentBox.hidden = !show;
      if (listSection) listSection.hidden = show;
    }

    async function acceptConsent() {
      try {
        await postJson('/consent', {});
        consented = true;
        showConsent(false);
        say('');
        if (pendingDevice) {
          pendingDevice = false;
          await saveDevicePhotos();
        }
        if (pendingFile) {
          var file = pendingFile;
          pendingFile = null;
          await handleFile(file);
        }
      } catch (error) {
        say(error.message, true);
      }
    }

    function cancelConsent() {
      pendingFile = null;
      showConsent(false);
      say('');
      if (input) input.value = '';
      /* LSC - Declining keeps the device photos. The card stays available so
         the shopper can save them later. */
      if (pendingDevice) {
        pendingDevice = false;
        deviceRecords().then(function (records) {
          if (records.length) showDeviceCard(records.length);
        });
      }
    }

    /* -------------------------------------------------------- uploading - */

    async function handleFile(file) {
      if (!file || busy) return;

      // LSC - Refuse at the limit before the crop editor opens.
      if (atLimit()) {
        say(limitMessage(), true);
        if (input) input.value = '';
        return;
      }

      if (!consented) {
        pendingFile = file;
        showConsent(true);
        return;
      }

      if (!/^image\//.test(file.type)) {
        say('That file is not an image.', true);
        if (input) input.value = '';
        return;
      }

      busy = true;
      say('Preparing your photo…');
      if (addLabel) addLabel.setAttribute('aria-busy', 'true');

      try {
        // Read the date BEFORE anything re-encodes the file and drops EXIF.
        var takenAt = (await readExifDate(file)) ||
          Math.floor((file.lastModified || Date.now()) / 1000);

        var source = await decodeImage(file, EDITOR_SOURCE_EDGE);
        say('');

        var result = await openCropEditor(source, null, TEXT.editorTitleNew);
        if (!result) {
          say('');
          return;
        }

        say(TEXT.saving);
        /* LSC - Each render is held under its own byte budget, and throws
           a readable error if it cannot be. */
        var full = await renderCrop(source, result.crop, CROP_MAX_WIDTH, MAX_CROP_BYTES);
        var thumb = await renderCrop(source, result.crop, THUMB_WIDTH, MAX_THUMB_BYTES);
        var original = await renderWhole(source, ORIGINAL_EDGE, MAX_ORIGINAL_BYTES);

        if (guest) {
          /* LSC - Guest: keep it on this device. The same three images the
             Worker would store, so a later save to an account is identical
             to a normal upload. */
          var record = {
            id: newLocalId(),
            taken_at: takenAt,
            created_at: Math.floor(Date.now() / 1000),
            stored_at: Date.now(),
            crop: result.crop,
            full: full,
            thumb: thumb,
            original: original
          };
          await device.put(record);
          photos.push(localPhoto(record));
          sortPhotos(photos);
          render();
          say(photos.length >= 2 ? TEXT.guestAddedCompare : TEXT.guestAdded);
          return;
        }

        var saved = await postJson('/photos', {
          data: await blobToBase64(full),
          thumb: await blobToBase64(thumb),
          original: await blobToBase64(original),
          crop: result.crop,
          taken_at: takenAt
        });

        photos.push(saved.photo);
        sortPhotos(photos);
        render();
        say('Photo added.');
      } catch (error) {
        if (error.code === 'consent_required') {
          consented = false;
          pendingFile = file;
          showConsent(true);
          say('');
        } else {
          say(error.message, true);
        }
      } finally {
        busy = false;
        if (addLabel) addLabel.removeAttribute('aria-busy');
        if (input) input.value = '';
      }
    }

    /* ---------------------------------------------------------- editing - */

    /* LSC - Re-crop an existing photo from its uncropped original. For a
       device photo the original is a local object URL, which fetch reads the
       same way. */
    async function editPhoto(photo, trigger) {
      if (busy) return;
      busy = true;
      say(TEXT.opening);
      try {
        var response = await fetch(imageUrl('original', photo));
        if (!response.ok) throw new Error('This photo could not be opened.');
        var source = await decodeImage(await response.blob(), EDITOR_SOURCE_EDGE);
        say('');

        var result = await openCropEditor(source, photo.crop, TEXT.editorTitleEdit);
        if (!result) return;

        say(TEXT.saving);
        var full = await renderCrop(source, result.crop, CROP_MAX_WIDTH, MAX_CROP_BYTES);
        var thumb = await renderCrop(source, result.crop, THUMB_WIDTH, MAX_THUMB_BYTES);
        var updated;

        if (photo.local) {
          /* LSC - Guest: replace the crop on the device record. */
          var record = Object.assign({}, photo.record, { crop: result.crop, full: full, thumb: thumb });
          await device.put(record);
          releaseUrls(photo);
          updated = localPhoto(record);
        } else {
          var saved = await postJson('/photos/' + encodeURIComponent(photo.id), {
            data: await blobToBase64(full),
            thumb: await blobToBase64(thumb),
            crop: result.crop
          });
          updated = saved.photo;
          updated._v = Date.now();
        }

        photos = photos.map(function (item) {
          return item.id === updated.id ? updated : item;
        });
        sortPhotos(photos);
        render();
        say(TEXT.saved);
      } catch (error) {
        say(error.message, true);
      } finally {
        busy = false;
        // Return focus to the card's Edit button, which render() rebuilt.
        var again = grid && grid.querySelector('[data-progress-edit="' + photo.id + '"]');
        if (again) again.focus();
        else if (trigger && trigger.isConnected) trigger.focus();
      }
    }

    /* --------------------------------------------------------- deleting - */

    async function removePhoto(id) {
      try {
        var target = photos.find(function (photo) {
          return photo.id === id;
        });
        if (target && target.local) {
          /* LSC - Guest: delete from the device. */
          await device.remove(id);
          releaseUrls(target);
        } else {
          await api('/photos/' + encodeURIComponent(id), { method: 'DELETE' });
        }
        photos = photos.filter(function (photo) {
          return photo.id !== id;
        });
        selected = selected.filter(function (value) {
          return value !== id;
        });
        render();
        say('Photo deleted.');
      } catch (error) {
        say(error.message, true);
      }
    }

    /* -------------------------------------------------------- selecting - */

    function toggleSelected(id) {
      var index = selected.indexOf(id);
      if (index === -1) {
        selected.push(id);
        /* Comparing means two photos. Keep the two most recent choices. */
        if (selected.length > 2) selected.shift();
      } else {
        selected.splice(index, 1);
      }
      render();
    }

    /* ---------------------------------------------------------- compare - */

    function openCompare() {
      if (selected.length !== 2 || !comparePanel) return;

      /* Oldest on the left, newest on the right, which is the order people
         expect from a before and after. */
      var pair = selected
        .map(function (id) {
          return photos.find(function (photo) {
            return photo.id === id;
          });
        })
        .filter(Boolean)
        .sort(function (a, b) {
          return a.taken_at - b.taken_at;
        });

      if (pair.length !== 2) return;

      comparePanel.textContent = '';
      comparePanel.hidden = false;
      if (listSection) listSection.hidden = true;

      var wrap = el('div', 'lsc-progress__compare');

      var stage = el('div', 'lsc-progress__compare-stage');

      var after = el('img', 'lsc-progress__compare-img');
      after.src = imageUrl('photo', pair[1]);
      after.alt = 'Photo from ' + formatDate(pair[1].taken_at);
      stage.appendChild(after);

      var topBox = el('div', 'lsc-progress__compare-top');
      var before = el('img', 'lsc-progress__compare-img');
      before.src = imageUrl('photo', pair[0]);
      before.alt = 'Photo from ' + formatDate(pair[0].taken_at);
      topBox.appendChild(before);
      stage.appendChild(topBox);

      var range = el('input', 'lsc-progress__compare-range');
      range.type = 'range';
      range.min = '0';
      range.max = '100';
      range.value = '50';
      range.setAttribute('aria-label', 'Reveal more of the earlier or the later photo');
      stage.appendChild(range);

      var line = el('div', 'lsc-progress__compare-line');
      line.appendChild(el('span', 'lsc-progress__compare-grip', '↔'));
      stage.appendChild(line);

      function setPosition(value) {
        stage.style.setProperty('--lsc-pos', value + '%');
      }

      /* The clipped image has to keep the width of the stage, otherwise the
         two halves drift apart as the slider moves. */
      function syncStageWidth() {
        stage.style.setProperty('--lsc-stage-width', stage.clientWidth + 'px');
      }

      range.addEventListener('input', function () {
        setPosition(range.value);
      });

      /* LSC - 27 September 2026: on a phone, touching anywhere on the photo
         moves the divider there and it follows the finger. The range is
         invisible and covers the whole stage, and its thumb is styled 2px
         wide in lsc-progress.css, so the divider sits exactly under the
         finger. Before this, an iPhone only moved it when the invisible
         thumb happened to be grabbed. */
      touchSlider(range, 2);

      setPosition(range.value);
      syncStageWidth();
      if (window.ResizeObserver) {
        var observer = new ResizeObserver(syncStageWidth);
        observer.observe(stage);
      } else {
        window.addEventListener('resize', syncStageWidth);
      }

      wrap.appendChild(stage);

      var labels = el('div', 'lsc-progress__compare-labels');
      labels.appendChild(el('span', null, formatDate(pair[0].taken_at)));
      labels.appendChild(el('span', null, formatDate(pair[1].taken_at)));
      wrap.appendChild(labels);

      var actions = el('div', 'lsc-progress__compare-actions');
      var back = el('button', 'lsc-progress__btn lsc-progress__btn--secondary');
      back.type = 'button';
      back.appendChild(el('span', null, 'Back to photos'));
      back.addEventListener('click', closeCompare);
      actions.appendChild(back);
      wrap.appendChild(actions);

      comparePanel.appendChild(wrap);
      range.focus();
    }

    function closeCompare() {
      if (!comparePanel) return;
      comparePanel.hidden = true;
      comparePanel.textContent = '';
      if (listSection) listSection.hidden = false;
      if (compareButton) compareButton.focus();
    }

    /* ------------------------------------------------------------ render - */

    function render() {
      if (!grid) return;
      grid.textContent = '';

      photos.forEach(function (photo) {
        var isSelected = selected.indexOf(photo.id) !== -1;

        var card = el('li', 'lsc-progress__card' + (isSelected ? ' lsc-progress__card--selected' : ''));

        var thumbWrap = el('div', 'lsc-progress__thumb-wrap');

        var image = el('img', 'lsc-progress__thumb');
        /* LSC - 18 October 2026: tiles now stretch to fill the row (up to
           three across, each at most half the screen; see the grid rules in
           lsc-progress.css), so a tile can be 400px or more wide on a
           computer. The 300px thumbnail looks soft at that size, so the
           browser is offered the 1200px cropped photo as well and picks
           whichever covers the tile at the screen's pixel density. A small
           tile on a phone still loads the 30 KB thumbnail. `sizes` tells
           the browser how wide the tile will be before layout: half the
           screen for a single photo, otherwise the screen shared between
           the columns (less about 3rem of page margin and gaps, which only
           ever overstates the width a little). */
        var columns = Math.min(photos.length, 3);
        image.srcset = imageUrl('thumb', photo) + ' ' + THUMB_WIDTH + 'w, ' +
          imageUrl('photo', photo) + ' ' + CROP_MAX_WIDTH + 'w';
        image.sizes = columns === 1 ? '50vw' : 'calc((100vw - 3rem) / ' + columns + ')';
        image.src = imageUrl('thumb', photo);
        image.alt = 'Progress photo from ' + formatDate(photo.taken_at);
        image.loading = 'lazy';
        thumbWrap.appendChild(image);

        var select = el('button', 'lsc-progress__select');
        select.type = 'button';
        select.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        select.setAttribute(
          'aria-label',
          (isSelected ? 'Deselect' : 'Select') + ' photo from ' + formatDate(photo.taken_at)
        );
        select.addEventListener('click', function () {
          toggleSelected(photo.id);
        });
        thumbWrap.appendChild(select);

        thumbWrap.appendChild(el('span', 'lsc-progress__mark', isSelected ? '✓' : ''));
        card.appendChild(thumbWrap);

        var meta = el('div', 'lsc-progress__meta');
        meta.appendChild(el('span', 'lsc-progress__date', formatDate(photo.taken_at)));
        if (photo.note) meta.appendChild(el('p', 'lsc-progress__note', photo.note));
        card.appendChild(meta);

        var actions = el('div', 'lsc-progress__card-actions');

        var dateText = formatDate(photo.taken_at);

        /* LSC - 23 September 2026: Edit and Delete are icon buttons, each
           half the row. An icon has no words, so every button carries an
           aria-label for screen readers and a title for a hover tooltip.
           The visible state change on Delete is backed by a label change,
           so it is announced as well as shown. */

        // Edit reopens the crop editor on this photo's original.
        var edit = el('button', 'lsc-progress__action lsc-progress__edit');
        edit.type = 'button';
        edit.setAttribute('data-progress-edit', photo.id);
        edit.setAttribute('aria-label', TEXT.edit + ' photo from ' + dateText);
        edit.title = TEXT.edit;
        edit.appendChild(icon('edit'));
        edit.addEventListener('click', function () {
          editPhoto(photo, edit);
        });
        actions.appendChild(edit);

        var remove = el('button', 'lsc-progress__action lsc-progress__delete');
        remove.type = 'button';
        remove.setAttribute('aria-label', 'Delete photo from ' + dateText);
        remove.title = 'Delete';
        remove.appendChild(icon('trash'));

        /* Two taps rather than a browser confirm dialog. A native dialog
           blocks the page and reads badly on a phone. The first tap swaps
           the trash can for a tick on a darker red and changes the label.
           The second tap within four seconds deletes. */
        var armed = false;
        function setArmed(on) {
          armed = on;
          remove.textContent = '';
          remove.appendChild(icon(on ? 'confirm' : 'trash'));
          remove.classList.toggle('lsc-progress__delete--confirm', on);
          remove.setAttribute('aria-label', on
            ? 'Tap again to delete photo from ' + dateText
            : 'Delete photo from ' + dateText);
          remove.title = on ? 'Tap again to delete' : 'Delete';
        }
        remove.addEventListener('click', function () {
          if (!armed) {
            setArmed(true);
            say('Tap the red button again to delete this photo.');
            window.setTimeout(function () {
              if (remove.isConnected && armed) setArmed(false);
            }, 4000);
            return;
          }
          removePhoto(photo.id);
        });
        actions.appendChild(remove);
        card.appendChild(actions);

        grid.appendChild(card);
      });

      if (emptyNote) emptyNote.hidden = photos.length > 0;
      syncAddButton();
      if (compareButton) {
        compareButton.hidden = photos.length < 2;
        compareButton.disabled = selected.length !== 2;
        compareButton.textContent =
          selected.length === 2 ? 'Compare these two' : 'Select two photos to compare';
      }
    }

    /* -------------------------------------------------------------- wire - */

    if (input) {
      input.addEventListener('change', function () {
        handleFile(input.files && input.files[0]);
      });
    }
    if (consentAccept) consentAccept.addEventListener('click', acceptConsent);
    if (consentCancel) consentCancel.addEventListener('click', cancelConsent);
    if (compareButton) compareButton.addEventListener('click', openCompare);

    /* LSC - Create account / Sign in buttons on the guest card record the
       save intent before the browser leaves the page. */
    panel.querySelectorAll('[data-progress-save-intent]').forEach(function (link) {
      link.addEventListener('click', markSaveIntent);
    });

    if (deviceSave) {
      deviceSave.addEventListener('click', function () {
        if (!consented) {
          pendingDevice = true;
          hideDeviceCard();
          showConsent(true);
          return;
        }
        saveDevicePhotos();
      });
    }
    if (deviceDiscard) {
      deviceDiscard.addEventListener('click', async function () {
        await device.clear();
        clearSaveIntent();
        hideDeviceCard();
        say(TEXT.discarded);
      });
    }

    /* The photo tips (snippets/lsc-progress-guide.liquid) start open so a new
       shopper reads them before the first photo. Once the shopper has photos,
       fold them away so the photos come first. Runs once, on load, so a
       shopper who opens them again keeps them open. */
    function foldTips() {
      var tips = panel.querySelector('[data-progress-tips]');
      if (tips && photos.length > 0) tips.open = false;
    }

    /* --------------------------------------------------------------- load - */

    (async function load() {
      if (guest) {
        /* LSC - Guest: only what is on this device. No network at all. */
        try {
          var records = await deviceRecords();
          photos = sortPhotos(records.map(localPhoto));
        } catch (error) {
          photos = [];
        }
        render();
        foldTips();
        return;
      }

      try {
        // LSC - One request: photos, consent and the limit together.
        var listed = await api('/photos');
        photos = sortPhotos((listed && listed.photos) || []);
        if (listed && typeof listed.limit === 'number' && listed.limit > 0) limit = listed.limit;

        if (listed && typeof listed.consented === 'boolean') {
          consented = listed.consented;
        } else {
          // Older Worker without `consented` in the list: ask separately.
          var consentState = await api('/consent');
          consented = Boolean(consentState && consentState.consented);
        }
        render();
        foldTips();
      } catch (error) {
        /* The most likely cause by far is that the Worker route is not
           deployed yet. Say something a shopper can understand and leave the
           rest of the page working. */
        say('Progress photos are not available right now. Please try again later.', true);
        if (emptyNote) emptyNote.hidden = true;
        return;
      }

      /* LSC - Photos taken on this device before signing in. */
      await offerDevicePhotos();
    })();
  }

  function init() {
    /* LSC - Any progress panel on the page, rather than only one inside the
       account section. */
    document.querySelectorAll('[data-progress-panel]').forEach(initProgress);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* The theme editor re-renders the section on every settings change. The
     re-rendered panel is new markup, so the ready flag starts clear again. */
  document.addEventListener('shopify:section:load', init);
})();
